import { useMemo, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Typography } from '@mui/material';
import dayjs from 'dayjs';
import { ReactComponent as TriangleIcon } from '../../../assets/triangle-outlined.svg';
import { getColor } from '../../../style/utils';
import { Metrics, RunStatus, SourceFreshnessRunStatus } from '../../../types';
import { Flex, FlexCenter } from '../../Flex';
import ChartSelectionOverlay from '../ChartControls/ChartSelectionOverlay';
import ChartZoomControls from '../ChartControls/ChartZoomControls';
import {
  LegendWrapper,
  LegendItem,
  Divider,
  statusColorMap,
  Wrapper,
  Chart,
  AreaStatus,
  areaStatuses,
  Pole,
  resolveAnomalyColor,
} from '../FreshnessChartCommon';
import { LEGEND_SWATCH_RADIUS } from '../constants';
import { useDomChartZoomScroll } from '../hooks/useDomChartZoomScroll';
import AutoFreshnessChartBar from './AutoFreshnessChartBar';
import AutoFreshnessChartTooltip from './AutoFreshnessChartTooltip';
import {
  Pole as PoleType,
  TooltipData,
  AnomalyArea,
  UpdateArea,
  Timestamp,
} from './types';

const METRIC_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const MAX_POLES = 1000;
const DESIRED_LAST_BLOCK_PCT = 20;

const ColorArea = styled(Flex)<{ $color: AreaStatus }>`
  width: 9px;
  height: 9px;
  opacity: 1;
  border-radius: ${LEGEND_SWATCH_RADIUS};
  background: ${({ $color, theme }) => getColor(theme, statusColorMap[$color])};
`;

const PoleLegendIcon = () => (
  <Flex sx={{ height: '9px' }}>
    <Pole $color="end" $width={1} />
  </Flex>
);

const adaptSortedPole = (
  pole: PoleType,
  index: number,
  poles: PoleType[],
): PoleType => {
  const isLast = index === poles.length - 1;
  const previousPole = index === 0 ? null : poles[index - 1];
  const [chartStart, chartEnd] = [
    poles[0].timestamp,
    poles[poles.length - 1].timestamp,
  ];
  const totalSize = chartEnd - chartStart;
  const distanceFromEnd = chartEnd - pole.timestamp;
  const right = (distanceFromEnd / totalSize) * 100;
  const data = {
    ...pole,
    min_value: isLast ? pole.min_value : null,
    max_value: isLast ? pole.max_value : null,
    start_time: previousPole
      ? dayjs.unix(previousPole.timestamp).utc().format(METRIC_TIME_FORMAT)
      : undefined,
    end_time: dayjs.unix(pole.timestamp).utc().format(METRIC_TIME_FORMAT),
    right,
  };
  return data;
};

const getPoles = (metrics: Metrics[]): PoleType[] => {
  const poles = metrics.map((metric, index) => ({
    ...metric,
    id: metric.metric_id || index.toString(),
    right: 0,
    timestamp: dayjs.utc(metric.bucket_end, METRIC_TIME_FORMAT).unix(),
    timestampMillis: dayjs.utc(metric.bucket_end, METRIC_TIME_FORMAT).valueOf(),
  }));
  poles.sort((a, b) => a.timestampMillis - b.timestampMillis);
  return poles.map(adaptSortedPole);
};

type Props = {
  metrics: Metrics[];
  status: RunStatus;
  severity?: 'warn' | 'error';
  loading?: boolean;
};

const InnerAutoFreshnessChart = ({
  metrics,
  status,
  severity,
  loading,
}: Props) => {
  const [tooltipData, setTooltipData] = useState<TooltipData | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [highlightedPoleIds, setHighlightedPoleIds] = useState<string[]>([]);
  const [highlightedAreaId, setHighlightedAreaId] = useState<string>();
  const chartRef = useRef<HTMLDivElement>(null);
  const zoom = useDomChartZoomScroll({ chartRef });

  const [zoomInitiated, setZoomInitiated] = useState(false);
  const [timestamps, setTimestamps] = useState<Timestamp[]>([]);
  const [chartTopPosition, setChartTopPosition] = useState(192);

  const chartBarRef = useRef<HTMLDivElement>(null);
  const scrollPercentRef = useRef<number>(1);

  const onChartScroll = () => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    const scrollPercent =
      (chart.scrollLeft + chart.clientWidth) / chart.scrollWidth;
    scrollPercentRef.current = scrollPercent;
  };

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    const scrollLeft =
      scrollPercentRef.current * chart.scrollWidth - chart.clientWidth;
    chart.scrollTo({ left: scrollLeft });
  }, [zoom.zoom]);

  const allPoles = useMemo(() => {
    if (metrics.length > MAX_POLES) {
      return getPoles(
        metrics.slice(metrics.length - MAX_POLES, metrics.length),
      );
    }
    return getPoles(metrics);
  }, [metrics]);

  const [chartStart, chartEnd] = useMemo(
    () => [allPoles[0].timestamp, allPoles[allPoles.length - 1].timestamp],
    [allPoles],
  );

  const anomalyArea = useMemo<AnomalyArea>(() => {
    const excTimePole = allPoles[allPoles.length - 1];
    const lastUpdatePole = allPoles[allPoles.length - 2];
    const totalAreaValue = excTimePole.value || 0;
    const validAreaValue = excTimePole.max_value || 0;
    const hasAnomaly = totalAreaValue > validAreaValue;
    const anomalyAreaValue = hasAnomaly ? totalAreaValue - validAreaValue : 0;

    return {
      id: excTimePole.id,
      poleIds: [excTimePole.id, lastUpdatePole.id],
      tooltipData: {
        payload: excTimePole,
        valueColor: statusColorMap[status],
      },
      width: lastUpdatePole.right,
      anomalyPercent: hasAnomaly
        ? (anomalyAreaValue / totalAreaValue) * 100
        : 0,
      validPercent: hasAnomaly ? (validAreaValue / totalAreaValue) * 100 : 100,
    };
  }, [allPoles, status]);

  const updateAreas = useMemo<UpdateArea[]>(
    () =>
      allPoles.slice(0, -1).map((pole, index) => {
        const nextPole = allPoles[index + 1];
        return {
          id: nextPole.id,
          poleIds: [pole.id, nextPole.id],
          tooltipData: {
            payload: nextPole,
          },
          width: pole.right - nextPole.right,
          right: nextPole.right,
        };
      }),
    [allPoles],
  );

  useEffect(() => {
    const chartWidth = chartBarRef.current?.clientWidth || 0;
    if (!chartWidth) {
      setTimestamps([]);
    }
    const labelCount = Math.floor(chartWidth / 200);
    const allTimestamps = [];
    const step = Math.floor((chartEnd - chartStart) / labelCount);
    for (let i = 1; i < labelCount; i++) {
      const timestamp = chartStart + i * step;
      allTimestamps.push({ timestamp, left: (i / labelCount) * 100 });
    }
    setTimestamps(allTimestamps);
  }, [chartStart, chartEnd, chartBarRef.current?.clientWidth, zoom.zoom]);

  useEffect(() => {
    if (zoomInitiated || allPoles.length < 2) return;

    const lastBlockWidthPct =
      allPoles.length >= 2 ? allPoles[allPoles.length - 2].right : 100;

    if (lastBlockWidthPct < DESIRED_LAST_BLOCK_PCT) {
      const zoomFactor = DESIRED_LAST_BLOCK_PCT / lastBlockWidthPct;
      zoom.setZoomManually(zoomFactor, true);
    }

    setZoomInitiated(true);
  }, [allPoles, zoom, zoomInitiated]);

  // Calculate and update the chart bar's top position relative to the chart container for proper overlay positioning
  useEffect(() => {
    const calculateChartTopPosition = () => {
      const chartBarElement = chartBarRef.current;

      if (chartBarElement) {
        const topPosition = chartBarElement.offsetTop;
        setChartTopPosition(topPosition);
      }
    };

    calculateChartTopPosition();

    const chartElement = chartRef.current;
    let resizeObserver: ResizeObserver | null = null;

    if (chartElement) {
      resizeObserver = new ResizeObserver(() => {
        calculateChartTopPosition();
      });
      resizeObserver.observe(chartElement);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [zoom.zoom, zoomInitiated]);

  return (
    <Wrapper>
      <LegendWrapper>
        <LegendItem>
          <PoleLegendIcon />
          <Typography>Table updates (According to query history)</Typography>
        </LegendItem>
        <Divider />
        <LegendItem>
          <TriangleIcon style={{ flexShrink: 0 }} />
          <Typography>Test exe. time</Typography>
        </LegendItem>
        {areaStatuses.includes(status) &&
          anomalyArea.anomalyPercent > 0 &&
          !loading && (
            <>
              <Divider />
              <LegendItem>
                <ColorArea
                  $color={resolveAnomalyColor(
                    status,
                    severity,
                    anomalyArea.anomalyPercent,
                  )}
                />
                <Typography>Anomaly</Typography>
              </LegendItem>
            </>
          )}
        <LegendItem style={{ marginLeft: 'auto' }}>
          <ChartZoomControls
            loading={!!loading}
            onZoomIn={zoom.handleZoomIn}
            onZoomOut={zoom.handleZoomOut}
            onReset={zoom.resetZoom}
            disableZoomIn={zoom.isMaxZoom}
            disableZoomOut={zoom.isMinZoom}
            disableReset={zoom.isInitialZoom}
            vertical
          />
        </LegendItem>
      </LegendWrapper>
      <Chart
        $scroll={zoom.zoom !== 1}
        ref={chartRef}
        onScroll={onChartScroll}
        onMouseDown={zoom.handleMouseDown}
        onMouseMove={zoom.handleMouseMove}
        onMouseUp={zoom.handleMouseUp}
      >
        <ChartSelectionOverlay
          startX={zoom.selectStartX}
          currentX={zoom.selectCurrentX}
          top={chartTopPosition}
          height={60}
        />

        <AutoFreshnessChartBar
          ref={chartBarRef}
          zoom={zoom.zoom}
          allPoles={allPoles}
          anomalyArea={anomalyArea}
          updateAreas={updateAreas}
          timestamps={timestamps}
          status={status}
          severity={severity}
          loading={loading || !zoomInitiated}
          highlightedAreaId={highlightedAreaId}
          highlightedPoleIds={highlightedPoleIds}
          onMouseMove={(event) => {
            event.preventDefault();
            setTooltipPos({
              x: event.clientX,
              y: event.clientY,
            });
          }}
          onAreaHighlight={(area) => {
            if (zoom.selectStartX != null) {
              if (highlightedAreaId !== undefined) {
                setTooltipData(null);
                setHighlightedAreaId(undefined);
                setHighlightedPoleIds([]);
              }
              return;
            }

            setTooltipData(area?.tooltipData || null);
            setHighlightedAreaId(area?.id);
            setHighlightedPoleIds(area?.poleIds || []);
          }}
        />
        {!zoom.selectStartX && (
          <AutoFreshnessChartTooltip
            data={tooltipData}
            x={tooltipPos.x}
            y={tooltipPos.y}
            status={status as SourceFreshnessRunStatus}
            limits={{
              left:
                chartRef.current?.getBoundingClientRect().left ||
                Number.MIN_SAFE_INTEGER,
              right:
                chartRef.current?.getBoundingClientRect().right ||
                Number.MAX_SAFE_INTEGER,
            }}
            loading={loading}
          />
        )}
      </Chart>
    </Wrapper>
  );
};

const AutoFreshnessChart = ({ loading = false, ...props }: Props) => {
  if (props.metrics.length < 2) {
    return (
      <Wrapper>
        <FlexCenter sx={{ height: '56px' }}>
          <Typography sx={{ color: 'content.secondary' }}>
            Not enough data to calculate anomaly score
          </Typography>
        </FlexCenter>
      </Wrapper>
    );
  }
  return <InnerAutoFreshnessChart {...props} loading={loading} />;
};

export default AutoFreshnessChart;
